import { CREATE_SPRITE_WORKER_URL } from '../config.js';
import { fetchCache } from '../utils/fetchCache.js';
import {
  computeStandaloneImageCost,
  interpolateCreditTemplate,
} from './creditCostApi.js';
import { getUserCredits } from './videoScheduleApi.js';

export const cacheBustImageUrl = (url, version = Date.now()) => {
  if (typeof url !== 'string' || !url) return url;
  const glue = url.includes('?') ? '&' : '?';
  return `${url}${glue}v=${encodeURIComponent(String(version))}`;
};

export const standalonePromptText = (prompt) => {
  if (typeof prompt === 'string') return prompt.trim();
  if (prompt && typeof prompt === 'object' && typeof prompt.p === 'string') return prompt.p.trim();
  return '';
};

export const standaloneNegativeText = (prompt) => {
  if (!prompt || typeof prompt !== 'object' || typeof prompt.n !== 'string') return '';
  return prompt.n.trim();
};

export const standaloneLoraList = (activation) => {
  if (activation && typeof activation === 'object' && !Array.isArray(activation) && activation.high) {
    const id = activation.high?.id == null ? '' : String(activation.high.id).trim();
    if (!id) return [];
    const strengthRaw = Number(activation.high?.strength ?? activation.strength);
    return [{
      id,
      source: activation.high?.source || '',
      strength: Number.isFinite(strengthRaw) ? strengthRaw : 1,
    }];
  }
  const entries = Array.isArray(activation)
    ? activation
    : (activation && typeof activation === 'object' ? [activation] : []);
  return entries
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const id = entry.id == null ? '' : String(entry.id).trim();
      if (!id) return null;
      const strengthRaw = Number(entry.strength);
      const strength = Number.isFinite(strengthRaw) ? strengthRaw : 1;
      return { id, source: entry.source || '', strength };
    })
    .filter(Boolean);
};

export const hdUpgradeCost = ({ style, loraCount, width, height } = {}) => computeStandaloneImageCost({
  style,
  loraCount,
  width,
  height,
});

export const hdUpgradeCostLabel = ({ style, loraCount, width, height, template, available } = {}) => {
  const cost = hdUpgradeCost({ style, loraCount, width, height });
  const line = interpolateCreditTemplate(
    template || 'HD upgrade = {cost} credits (You have {available} credits)',
    { cost, available: available ?? getUserCredits(), count: 1, total: cost },
  );
  return { cost, label: line };
};

export const requestStandaloneHdUpgrade = async ({ token, outputKey }) => {
  if (!token || !outputKey) {
    throw new Error('Missing upgrade request');
  }
  return fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/upgrade-standalone-image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: { output_key: outputKey },
    skipCache: true,
  });
};

export const requestStandaloneImageDelete = async ({ token, outputKey }) => {
  if (!token || !outputKey) {
    throw new Error('Missing delete request');
  }
  return fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/delete-standalone-image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: { output_key: outputKey },
    skipCache: true,
  });
};

const standaloneObjectUrls = new Map();

export const standaloneImageRequestUrl = (outputKey) => (
  `${CREATE_SPRITE_WORKER_URL}/standalone-image/${encodeURIComponent(String(outputKey).toLowerCase())}`
);

export const revokeStandaloneImageObjectUrl = (outputKey) => {
  if (!outputKey) return;
  const key = String(outputKey).toLowerCase();
  const prev = standaloneObjectUrls.get(key);
  if (typeof prev === 'string' && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
  standaloneObjectUrls.delete(key);
};

export const fetchStandaloneImageObjectUrl = async ({ token, outputKey, bust = false } = {}) => {
  if (!token || !outputKey) return null;
  const key = String(outputKey).toLowerCase();
  if (bust) revokeStandaloneImageObjectUrl(key);
  const cached = standaloneObjectUrls.get(key);
  if (cached) return cached;
  const requestUrl = `${standaloneImageRequestUrl(key)}${bust ? `?v=${Date.now()}` : ''}`;
  const blob = await fetchCache.fetchBlob(requestUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const objectUrl = URL.createObjectURL(blob);
  standaloneObjectUrls.set(key, objectUrl);
  return objectUrl;
};
